import { useEffect, useState } from "react";
import "./checkoutsummercamp.css";
import { useSummerCamp } from "../../../APIContext/summerCampContext";
import OrderSummary from "./OrderSummary";
import GuestForm from "./GuestForm";
import { useNewUser } from "../../../APIContext/NewUserContext";

const SummerCampPaymentCheckout = () => {
  const {
    getOfferingCart,
    applyCampCoupon,
    loading,
    deleteOfferingById,
    removeCampCoupon,
    offeringCheckout,
  } = useSummerCamp();

  const {
    proceedToOfferingCheckoutGuest,
    applyCampCouponGust,
    removeCampCouponGust,
  } = useNewUser();

  const [selectedCoupon, setSelectedCoupon] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [couponMessage, setCouponMessage] = useState({ text: "", type: "" });
  const [summerCampCartItems, setSummerCampCartItems] = useState([]);
  const [isConsentChecked, setIsConsentChecked] = useState(false);

  // Validation State for Guest Form
  const [guestValidationErrors, setGuestValidationErrors] = useState([]);

  // Guest Form State
  const [guestFormData, setGuestFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    address: "",
    phone: "",
  });

  const user = localStorage.getItem("user");

  // --- Fetch Cart Data & Pre-fill Coupon ---
  const fetchCart = async () => {
    const data = await getOfferingCart();

    if (Array.isArray(data) && data.length > 0) {
      setSummerCampCartItems(data);

      const activeItem = data[0];
      if (activeItem.coupon && activeItem.coupon.code) {
        setSelectedCoupon(activeItem.coupon.code);
      }
    } else {
      setSummerCampCartItems([]);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  // --- Handlers ---

  const handleGuestChange = (e) => {
    setGuestFormData({ ...guestFormData, [e.target.name]: e.target.value });
  };

  const handleApplyCoupon = async () => {
    if (!selectedCoupon) return;

    if (summerCampCartItems.length === 0) {
      setCouponMessage({ text: "Cart is empty", type: "error" });
      return;
    }

    setIsApplying(true);
    setCouponMessage({ text: "", type: "" });

    const targetCartId = summerCampCartItems[0].id;

    const payload = {
      cart_id: targetCartId,
      code: selectedCoupon,
    };

    let response = null;
    if (user) {
      await applyCampCoupon(payload);
    } else {
      response = await applyCampCouponGust(payload);
    }

    if (response && (response.success || response.coupon)) {
      await fetchCart();
      setCouponMessage({ text: "", type: "" });
    } else {
      setCouponMessage({
        text: response?.message || "Invalid Coupon Code",
        type: "error",
      });
    }

    setIsApplying(false);
  };

  const handleSubmitAuthenticatedUser = async () => {
    await offeringCheckout(summerCampCartItems?.[0]?.id, selectedCoupon);
  };

  const handleRemoveCoupon = async () => {
    const targetCartId = summerCampCartItems[0].id;
    if (user) {
      await removeCampCoupon(targetCartId);
    } else {
      await removeCampCouponGust(targetCartId);
    }
    setSelectedCoupon("");
    setCouponMessage({ text: "", type: "" });
    await fetchCart();
  };

  const handleRemoveItem = async (id) => {
    const res = await deleteOfferingById(id);
    if (res) fetchCart();
  };

  // --- Validation Logic ---
  const validateGuestForm = () => {
    const errors = [];

    if (!guestFormData.firstName.trim()) errors.push("First Name is required");
    if (!guestFormData.lastName.trim()) errors.push("Last Name is required");
    if (!guestFormData.username.trim()) errors.push("Username is required");
    if (!guestFormData.address.trim()) errors.push("Address is required");
    if (!guestFormData.phone.trim()) errors.push("Phone is required");

    if (!guestFormData.email.trim()) {
      errors.push("Email is required");
    } else if (!/\S+@\S+\.\S+/.test(guestFormData.email)) {
      errors.push("Email address is invalid");
    }

    if (!guestFormData.password) errors.push("Password is required");
    if (!guestFormData.confirmPassword)
      errors.push("Confirm Password is required");

    if (guestFormData.password && guestFormData.confirmPassword) {
      if (guestFormData.password !== guestFormData.confirmPassword) {
        errors.push("Passwords do not match");
      }
    }

    return errors;
  };

  const handleSubmit = async () => {
    //  Check if Guest User Validation is needed
    if (!user) {
      const errors = validateGuestForm();
      if (errors.length > 0) {
        setGuestValidationErrors(errors);
        // Scroll to top to see errors
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      // Clear errors if validation passes
      setGuestValidationErrors([]);
    }
    const payload = {
      cart_id: summerCampCartItems?.[0]?.id,
      parent: {
        email: guestFormData.email,
        username: guestFormData.username,
        password: guestFormData.password,
        first_name: guestFormData.firstName,
        last_name: guestFormData.lastName,
        phone: guestFormData.phone,
        address: guestFormData.address,
      },
    };
    const result = await proceedToOfferingCheckoutGuest(payload);
  };

  // Calculate Total for Guest Button (Using final_price if available, else total_price)
  const cartTotal = summerCampCartItems.reduce((acc, item) => {
    const price = item.final_price
      ? parseFloat(item.final_price)
      : parseFloat(item.total_price || 0);
    return acc + price;
  }, 0);

  return (
    <div className="bg-custom brightness d-flex justify-content-center">
      <div
        className={`checkout-wrapper ${!user ? "split-layout" : "single-layout"}`}
      >
        {!user ? (
          /* GUEST LAYOUT */
          <>
            <div className="layout-col left">
              <GuestForm
                handleGuestChange={handleGuestChange}
                handleSubmit={handleSubmit}
                cartTotal={cartTotal}
                validationErrors={guestValidationErrors}
                setIsConsentCheckedprop={isConsentChecked}
              />
            </div>
            <div className="layout-col right">
              <OrderSummary
                cartItems={summerCampCartItems}
                user={user}
                selectedCoupon={selectedCoupon}
                setSelectedCoupon={setSelectedCoupon}
                handleApplyCoupon={handleApplyCoupon}
                handleRemoveCoupon={handleRemoveCoupon}
                handleRemoveItem={handleRemoveItem}
                isApplying={isApplying}
                loading={loading}
                couponMessage={couponMessage}
                handleSubmit={handleSubmit}
                setIsConsentCheckedprop={setIsConsentChecked}
              />
            </div>
          </>
        ) : (
          /* AUTHENTICATED LAYOUT */
          <div className="layout-col center">
            <OrderSummary
              cartItems={summerCampCartItems}
              user={user}
              selectedCoupon={selectedCoupon}
              setSelectedCoupon={setSelectedCoupon}
              handleApplyCoupon={handleApplyCoupon}
              handleRemoveCoupon={handleRemoveCoupon}
              handleRemoveItem={handleRemoveItem}
              isApplying={isApplying}
              loading={loading}
              couponMessage={couponMessage}
              handleSubmit={handleSubmitAuthenticatedUser}
              setIsConsentCheckedprop={setIsConsentChecked}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SummerCampPaymentCheckout;
